<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
    <div class="panel mb-8">
      <div class="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
            Minhas <span class="bg-clip-text text-transparent bg-gradient-to-r from-[#7B2CBF] to-[#A367E1]">Assinaturas</span>
          </h1>
          <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Gerencie e analise os gastos com seus serviços.
          </p>
        </div>
        <button @click="handleNovaAssinatura" class="btn btn-primary">
          <span class="material-symbols-outlined mr-2">add_card</span>
          Nova Assinatura
        </button>
      </div>

      <div class="mt-6">
        <nav class="segmented-control" role="tablist">
          <button @click="activeTab = 'list'" :class="{ 'active': activeTab === 'list' }" role="tab">
            <span class="material-symbols-outlined">list_alt</span>
            Lista
          </button>
          <button @click="activeTab = 'analytics'" :class="{ 'active': activeTab === 'analytics' }" role="tab">
            <span class="material-symbols-outlined">monitoring</span>
            Relatórios
          </button>
        </nav>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 'list'" key="list">
        <div v-if="assinaturasParaExibir.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="item in assinaturasParaExibir" :key="item.id" class="card">
            <a :href="getPlatformStyle(item).url" target="_blank" rel="noopener noreferrer" class="card-header" :style="{ backgroundColor: getPlatformColor(item) }">
              <div class="size-10 rounded-lg bg-white/10 grid place-items-center ring-1 ring-white/20 overflow-hidden">
                <img :src="getPlatformStyle(item).iconUrl" :alt="item.nomeStreamer" class="w-6 h-6 object-contain" />
              </div>
              <div class="min-w-0 text-white">
                <p class="font-semibold leading-tight truncate">{{ item.nomeStreamer }}</p>
                <p class="text-xs opacity-90 truncate">{{ item.plataforma || 'Serviço' }}</p>
              </div>
              <span class="ml-auto material-symbols-outlined text-white/90 text-base">open_in_new</span>
            </a>
            <div class="p-5 space-y-5">
              <div class="flex items-start justify-between">
                <div>
                  <p class="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Mensal</p>
                  <p class="mt-1 text-3xl font-bold text-slate-900 dark:text-white tabular-nums">{{ formatCurrency(item.valor) }}</p>
                </div>
                <span class="badge" :class="getStatusClass(item.status, item.isOverdue).bg">
                  {{ item.isOverdue ? 'VENCIDA' : item.status }}
                </span>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div class="info-item">
                  <span class="material-symbols-outlined info-icon">calendar_month</span>
                  <div>
                    <p class="info-k">Início</p>
                    <p class="info-v">{{ formatDate(item.dataInicio) }}</p>
                  </div>
                </div>
                <div class="info-item">
                  <span class="material-symbols-outlined info-icon">event_upcoming</span>
                  <div>
                    <p class="info-k">{{ getVencimentoLabel(item.status) }}</p>
                    <p class="info-v">{{ formatDate(item.dataFim) }}</p>
                  </div>
                </div>
              </div>
              <div class="flex items-center justify-end gap-2 pt-2 border-t border-slate-200/80 dark:border-slate-700/60">
                <button v-if="item.status === 'ATIVA'" @click="confirmarCancelamento(item)" class="btn btn-secondary btn-sm">
                  Cancelar
                </button>
                <div class="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700/50 p-1">
                  <button v-if="item.status === 'ATIVA'" @click="handleEditAssinatura(item)" title="Editar" class="btn-action-icon text-sky-600 dark:text-sky-400">
                    <span class="material-symbols-outlined text-lg">edit</span>
                  </button>
                  <button @click="confirmarExclusao(item)" title="Excluir" class="btn-action-icon text-rose-600 dark:text-rose-400">
                    <span class="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="panel text-center px-8 py-12 space-y-4">
          <div class="mx-auto size-14 rounded-xl bg-slate-100 dark:bg-slate-800 grid place-items-center">
            <span class="material-symbols-outlined text-3xl text-slate-500 dark:text-slate-400">subscriptions</span>
          </div>
          <p class="text-slate-600 dark:text-slate-400">Nenhuma assinatura cadastrada.</p>
          <button @click="handleNovaAssinatura" class="btn btn-primary">Adicionar Assinatura</button>
        </div>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 'analytics'" key="analytics" class="space-y-8">
        <section class="space-y-6">
          <header class="section-head">
            <h2 class="section-title">Visão Geral</h2>
          </header>
          <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div class="lg:col-span-2 space-y-6">
              <div class="panel p-6 space-y-5">
                <h3 class="panel-title">Gasto Mensal Atual</h3>
                <p class="text-5xl font-bold text-slate-900 dark:text-white tabular-nums">
                  {{ formatCurrency(totalGastoMensalAtivo) }}
                </p>
                <p class="text-sm text-slate-600 dark:text-slate-400">Soma de todas as assinaturas ativas.</p>
              </div>
              <div class="panel p-0 overflow-hidden">
                <div class="p-6 pb-3">
                  <h3 class="panel-title">Próximas Renovações (30 dias)</h3>
                </div>
                <ul v-if="proximasRenovacoes.length > 0" class="divide-y divide-slate-200/80 dark:divide-slate-700/60">
                  <li v-for="item in proximasRenovacoes" :key="item.id" class="flex items-center justify-between px-6 py-4">
                    <div class="flex items-center gap-4 min-w-0">
                      <img :src="getPlatformStyle(item).iconUrl" class="size-8 rounded-md object-contain ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-800 p-1" />
                      <p class="font-medium text-slate-800 dark:text-slate-200 truncate">{{ item.nomeStreamer }}</p>
                    </div>
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ formatDate(item.dataFim) }}</span>
                  </li>
                </ul>
                <p v-else class="px-6 py-8 text-sm text-center text-slate-600 dark:text-slate-400">
                  Sem renovações nos próximos 30 dias.
                </p>
              </div>
            </div>
            <div class="lg:col-span-3 panel p-6 space-y-5">
              <h3 class="panel-title">Gastos por Serviço</h3>
              <div class="h-[380px]">
                <Apexchart v-if="chartSeriesRadial.length > 0" type="radialBar" height="100%" :options="chartOptionsRadialBar" :series="chartSeriesRadial" />
                <div v-else class="empty-state h-full">
                  <span class="material-symbols-outlined text-4xl">pie_chart</span>
                  <span>Sem dados para exibir.</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section class="space-y-6">
          <header class="section-head">
            <h2 class="section-title">Projeções</h2>
          </header>
          <div class="panel p-6 space-y-5">
            <h3 class="panel-title">Previsão dos Próximos 6 Meses</h3>
            <div class="h-80">
              <Bar v-if="previsaoAnual.meses.length > 0" :data="barChartData" :options="barChartOptions" />
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-4xl">monitoring</span>
                <span>Sem dados suficientes.</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showModal" @click.self="showModal = false" class="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-lg">
          <header class="modal-header">
            <h3 class="modal-title">{{ isEditing ? 'Editar Assinatura' : 'Nova Assinatura' }}</h3>
            <button @click="showModal = false" class="btn-action-icon"><span class="material-symbols-outlined">close</span></button>
          </header>
          <form @submit.prevent="handleSubmit" class="p-6 space-y-5">
            <div>
              <label class="form-label">Streamer/Serviço</label>
              <select v-model="assinaturaModel.nomeStreamer" required class="form-select">
                <option value="" selected>Selecione</option>
                <option value="Outro">Outro / Não listado</option>
                <option v-for="platform in availablePlatforms" :key="platform" :value="platform">{{ platform }}</option>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-5">
              <div>
                <label class="form-label">Plataforma</label>
                <input v-model="assinaturaModel.plataforma" type="text" placeholder="Ex: Streaming" class="form-input" />
              </div>
              <div>
                <label class="form-label">Valor Mensal (R$)</label>
                <input v-model.number="assinaturaModel.valor" type="number" step="0.01" required placeholder="0,00" class="form-input" />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-5">
              <div>
                <label class="form-label">Data de Início</label>
                <input v-model="assinaturaModel.dataInicio" type="date" required class="form-input" />
              </div>
              <div>
                <label class="form-label">Próximo Vencimento</label>
                <input v-model="assinaturaModel.dataFim" type="date" required class="form-input" />
              </div>
            </div>
            <footer class="pt-2 flex justify-end gap-3">
              <button type="button" @click="showModal = false" class="btn btn-secondary">Cancelar</button>
              <button type="submit" class="btn btn-primary">{{ isEditing ? 'Salvar' : 'Adicionar' }}</button>
            </footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showConfirmModal" @click.self="showConfirmModal = false" class="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-md w-full">
          <header class="modal-header">
            <h3 class="modal-title">{{ confirmProps.title }}</h3>
            <button class="btn-action-icon" @click="showConfirmModal = false">
              <span class="material-symbols-outlined">close</span>
            </button>
          </header>
          <div class="p-6 text-center space-y-4">
            <span
              class="material-symbols-outlined text-5xl mx-auto"
              :class="confirmProps.intent === 'danger' ? 'text-rose-500 dark:text-rose-400' : 'text-amber-500 dark:text-amber-400'"
            >
              {{ confirmProps.icon || (confirmProps.intent === 'danger' ? 'delete_forever' : 'warning') }}
            </span>
            <p class="text-sm text-slate-600 dark:text-slate-400">
              {{ confirmProps.message }}
            </p>
          </div>
          <div class="px-6 pb-6 grid grid-cols-2 gap-3">
            <button class="btn btn-secondary" @click="showConfirmModal = false">Cancelar</button>
            <button
              class="btn"
              :class="confirmProps.intent === 'danger' ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'btn-primary'"
              @click="executeConfirm"
            >
              {{ confirmProps.confirmText || 'Confirmar' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, defineAsyncComponent, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'

const Bar = defineAsyncComponent(() => import('vue-chartjs').then(m => m.Bar))
const Apexchart = defineAsyncComponent(() => import('vue3-apexcharts'))

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)
const router = useRouter()

const assinaturas = ref([])
const activeTab = ref('list')

const isMobile = ref(false)
const checkDevice = () => {
  isMobile.value = window.innerWidth < 768
}

const showModal = ref(false)
const isEditing = ref(false)
const getInitialModel = () => ({ id: null, nomeStreamer: '', plataforma: '', valor: null, dataInicio: new Date().toISOString().split('T')[0], dataFim: '' })
const assinaturaModel = ref(getInitialModel())

watch(() => assinaturaModel.value.dataInicio, (nova) => {
  if (showModal.value && !isEditing.value && nova) {
    const d = new Date(nova + 'T12:00:00')
    const next = new Date(d.setMonth(d.getMonth() + 1))
    assinaturaModel.value.dataFim = next.toISOString().split('T')[0]
  }
})

const resetForm = () => { assinaturaModel.value = getInitialModel(); isEditing.value = false }
const showAddModal = () => { resetForm(); showModal.value = true }
const editAssinatura = (item) => { isEditing.value = true; assinaturaModel.value = { ...item }; showModal.value = true }

const handleSubmit = async () => {
  try {
    if (isEditing.value) await axios.put(`${apiUrl}/${assinaturaModel.value.id}`, assinaturaModel.value, authConfig())
    else await axios.post(apiUrl, assinaturaModel.value, authConfig())
    showModal.value = false
    await verificarERenovarAssinaturas()
  } catch { alert('Não foi possível salvar a assinatura.') }
}

const handleNovaAssinatura = () => {
  if (isMobile.value) {
    router.push({ name: 'SubscriptionAdd' })
  } else {
    showAddModal()
  }
}

const handleEditAssinatura = (item) => {
  if (isMobile.value) {
    router.push({ name: 'SubscriptionEdit', params: { id: item.id } })
  } else {
    editAssinatura(item)
  }
}

const showConfirmModal = ref(false)
const confirmProps = ref({ title: '', message: '', confirmText: '', intent: 'warning', icon: 'warning' })
const confirmAction = ref(null)

const executeConfirm = () => {
  if (typeof confirmAction.value === 'function') {
    confirmAction.value()
  }
  showConfirmModal.value = false
}

const platformStyles = {
  'controlease': { bg: 'bg-slate-900 text-white', iconUrl: 'https://i.ibb.co/Fkst3gVr/Black-and-Beige-Minimalist-Aesthetic-Modern-Simple-Typography-Agency-Logo-13.png', url: 'https://controlease.me', color: { light: '#111827', dark: '#111827' } },
  'chatgpt': { bg: 'bg-emerald-600 text-white', iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg', url: 'https://chat.openai.com', color: { light: '#059669', dark: '#10B981' } },
  'google one': { bg: 'bg-slate-600 text-white', iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/768px-Google_%22G%22_logo.svg.png', url: 'https://one.google.com', color: { light: '#2563EB', dark: '#3B82F6' } },
  'microsoft 365': { bg: 'bg-indigo-600 text-white', iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Microsoft_365_%282022%29.svg/931px-Microsoft_365_%282022%29.svg.png', url: 'https://www.microsoft.com/microsoft-365', color: { light: '#6366F1', dark: '#818CF8' } },
  'adobe': { bg: 'bg-red-600 text-white', iconUrl: 'https://logodownload.org/wp-content/uploads/2017/04/adobe-logo-0.png', url: 'https://www.adobe.com/creativecloud.html', color: { light: '#DC2626', dark: '#EF4444' } },
  'canva': { bg: 'bg-sky-600 text-white', iconUrl: 'https://www.luiconceitocriativo.com/wp-content/uploads/2024/08/244421955_1062499494497845_7683090676434840618_n-1024x1024.jpg', url: 'https://www.canva.com', color: { light: '#0284C7', dark: '#0EA5E9' } },
  'default': { bg: 'bg-slate-500 text-white', iconUrl: 'https://cdn-icons-png.flaticon.com/512/681/681458.png', url: '#', color: { light: '#64748B', dark: '#94A3B8' } },
  'netflix': { bg: 'bg-red-700 text-white', iconUrl: 'https://images.ctfassets.net/4cd45et68cgf/Rx83JoRDMkYNlMC9MKzcB/2b14d5a59fc3937afd3f03191e19502d/Netflix-Symbol.png?w=700&h=456', url: 'https://www.netflix.com', color: { light: '#B91C1C', dark: '#DC2626' } },
  'spotify': { bg: 'bg-emerald-600 text-white', iconUrl: 'https://images.icon-icons.com/3261/PNG/512/spotify_logo_icon_206676.png', url: 'https://www.spotify.com', color: { light: '#059669', dark: '#10B981' } },
  'twitch': { bg: 'bg-purple-700 text-white', iconUrl: 'httpsimg.icons8.com/ios11/512/FFFFFF/twitch.png', url: 'https://www.twitch.tv', color: { light: '#6D28D9', dark: '#8B5CF6' } },
  'youtube': { bg: 'bg-red-600 text-white', iconUrl: 'https://logodownload.org/wp-content/uploads/2014/10/youtube-logo-5-2.png', url: 'https://www.youtube.com', color: { light: '#DC2626', dark: '#EF4444' } },
  'prime video': { bg: 'bg-sky-600 text-white', iconUrl: 'https://m.media-amazon.com/images/I/31W9hs7w0JL.png', url: 'https://www.primevideo.com', color: { light: '#0284C7', dark: '#0EA5E9' } },
  'disney': { bg: 'bg-blue-900 text-white', iconUrl: 'https://images.seeklogo.com/logo-png/4/1/disney-logo-png_seeklogo-41972.png', url: 'https://www.disneyplus.com', color: { light: '#1E3A8A', dark: '#1D4ED8' } },
  'max': { bg: 'bg-indigo-700 text-white', iconUrl: 'https://images-wixmp-ed30a86b8c4ca887773594c2.wixmp.com/f/5fbef0ad-821e-42ff-bd50-c54a0742abc7/dfudrq1-f161a753-d067-4c18-84f6-ca87785a41fe.png', url: 'https://www.max.com', color: { light: '#6D28D9', dark: '#8B5CF6' } },
  'crunchyroll': { bg: 'bg-orange-600 text-white', iconUrl: 'https://img.icons8.com/m_rounded/512/FFFFFF/crunchyroll.png', url: 'https://www.crunchyroll.com', color: { light: '#EA580C', dark: '#F97316' } },
  'paramount': { bg: 'bg-blue-700 text-white', iconUrl: 'https://wallpapers.com/images/hd/paramount-picture-rapar3xy1d9cfv3q.jpg', url: 'https://www.paramountplus.com', color: { light: '#1D4ED8', dark: '#3B82F6' } },
  'apple tv': { bg: 'bg-neutral-800 text-white', iconUrl: 'https://hyken.com/wp-content/uploads/2023/01/Apple-TV-Logo-2019-present.png', url: 'https://tv.apple.com', color: { light: '#262626', dark: '#404040' } },
  'globoplay': { bg: 'bg-rose-600 text-white', iconUrl: 'https://m.media-amazon.com/images/I/71bch7gUsqL.png', url: 'https://globoplay.globo.com', color: { light: '#E11D48', dark: '#FB7185' } },
  'r7': { bg: 'bg-indigo-700 text-white', iconUrl: 'https://play-lh.googleusercontent.com/EuDnuqP6BMGuHHu6B8uh9T24_fKlTRZwjJHWyS_HIqGXYc7I5zuCQMswbaHCVNYPlos', url: 'https://www.playplus.com', color: { light: '#1D4ED8', dark: '#3B82F6' } },
  'deezer': { bg: 'bg-fuchsia-600 text-white', iconUrl: 'https://imagens.mobiaccess.com.br/deezer_logo_vertical.png', url: 'https://www.deezer.com', color: { light: '#C026D3', dark: '#E879F9' } }
}

const capitalize = (str) => !str ? '' : str.split(' ').map(w => (['tv+', 'one', '365'].includes(w.toLowerCase()) ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase())).join(' ')
const availablePlatforms = computed(() => Object.keys(platformStyles).filter(k => k !== 'default').map(capitalize).sort())

const assinaturasParaExibir = computed(() =>
  assinaturas.value.map(ass => {
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0)
    const fim = new Date(ass.dataFim)
    const isOverdue = ass.status === 'ATIVA' && fim < hoje
    return isOverdue ? { ...ass, status: 'VENCIDA', isOverdue: true } : { ...ass, isOverdue: false }
  }).sort((a, b) => new Date(a.dataFim) - new Date(b.dataFim))
)

const getPlatformStyle = (assinatura) => {
  const name = (assinatura.nomeStreamer || '').toLowerCase()
  for (const key in platformStyles) if (name.includes(key)) return platformStyles[key]
  return platformStyles.default
}
const getPlatformColor = (assinatura) => {
  const style = getPlatformStyle(assinatura);
  return (style.color || platformStyles.default.color)[isDarkMode.value ? 'dark' : 'light'];
}

const apiUrl = 'https://backend.controlease.com.br/assinaturas-streamer'
const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const fetchAssinaturas = async () => {
  try { const { data } = await axios.get(apiUrl, authConfig()); assinaturas.value = data || [] }
  catch { assinaturas.value = [] }
}

const verificarERenovarAssinaturas = async () => {
  try {
    await axios.post(`${apiUrl}/verificar-renovar`, {}, authConfig())
    await fetchAssinaturas()
  } catch {
    await fetchAssinaturas()
  }
}

const confirmarCancelamento = (item) => {
  confirmProps.value = {
    title: 'Cancelar assinatura?',
    message: 'Sua assinatura será marcada como cancelada e não será renovada.',
    confirmText: 'Sim, Cancelar',
    intent: 'warning',
    icon: 'warning'
  }
  confirmAction.value = () => executeCancel(item.id)
  showConfirmModal.value = true
}

const executeCancel = async (id) => {
  if (!id) return
  try { await axios.put(`${apiUrl}/${id}/cancelar`, {}, authConfig()); await verificarERenovarAssinaturas() }
  catch { alert('Não foi possível cancelar a assinatura.') }
  finally { confirmAction.value = null }
}

const confirmarExclusao = (item) => {
  confirmProps.value = {
    title: 'Excluir assinatura?',
    message: 'Esta ação é permanente e não pode ser desfeita.',
    confirmText: 'Sim, Excluir',
    intent: 'danger',
    icon: 'delete_forever'
  }
  confirmAction.value = () => executeDelete(item.id)
  showConfirmModal.value = true
}

const executeDelete = async (id) => {
  if (!id) return
  try { await axios.delete(`${apiUrl}/${id}`, authConfig()); await verificarERenovarAssinaturas() }
  catch { alert('Não foi possível excluir a assinatura.') }
  finally { confirmAction.value = null }
}

const getVencimentoLabel = (status) => status === 'ATIVA' ? 'Próx. Venc.' : status === 'CANCELADA' ? 'Expira em' : 'Venceu em'
const formatCurrency = (v) => (typeof v === 'number' ? v : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const formatDate = (s) => { if (!s) return 'N/A'; const d = new Date(s + 'T12:00:00'); return d.toLocaleDateString('pt-BR') }

const getStatusClass = (st, overdue = false) => {
  if (overdue) return { bg: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 ring-amber-300 dark:ring-amber-700' }
  if (st === 'ATIVA') return { bg: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 ring-emerald-300 dark:ring-emerald-700' }
  return { bg: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 ring-slate-300 dark:ring-slate-700' }
}

const isDarkMode = ref(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
const ativas = computed(() => assinaturas.value.filter(a => a.status === 'ATIVA'))
const totalGastoMensalAtivo = computed(() => ativas.value.reduce((t, i) => t + (i.valor || 0), 0))

const proximasRenovacoes = computed(() => {
  const hoje = new Date(), futuro = new Date()
  hoje.setHours(0, 0, 0, 0)
  futuro.setDate(hoje.getDate() + 30)
  return ativas.value
    .filter(a => { const d = new Date(a.dataFim + 'T12:00:00'); return d >= hoje && d <= futuro })
    .sort((a, b) => new Date(a.dataFim) - new Date(b.dataFim))
})

const gastoPorServico = computed(() => {
  const out = {}
  ativas.value.forEach(i => { const k = i.nomeStreamer || 'Outros'; out[k] = (out[k] || 0) + (i.valor || 0) })
  return out
})

const chartSeriesRadial = computed(() => {
  const total = totalGastoMensalAtivo.value
  if (!total) return []
  return Object.values(gastoPorServico.value).map(v => (v / total) * 100)
})

const chartOptionsRadialBar = computed(() => ({
  chart: { type: 'radialBar', background: 'transparent', toolbar: { show: false } },
  plotOptions: {
    radialBar: {
      hollow: { size: '28%' },
      track: { background: isDarkMode.value ? '#334155' : '#F1F5F9' },
      dataLabels: {
        name: { show: false },
        value: { show: false },
        total: { show: true, label: 'GASTO MENSAL', color: isDarkMode.value ? '#9CA3AF' : '#64748B', formatter: () => formatCurrency(totalGastoMensalAtivo.value) }
      }
    }
  },
  colors: Object.keys(gastoPorServico.value).map(n => getPlatformColor({ nomeStreamer: n })),
  labels: Object.keys(gastoPorServico.value),
  legend: { show: true, position: 'right', fontSize: '14px', fontFamily: 'Inter, sans-serif', labels: { colors: isDarkMode.value ? '#9CA3AF' : '#475569' }, itemMargin: { vertical: 4 } },
  stroke: { lineCap: 'round' }
}))

const previsaoAnual = computed(() => {
  const meses = [], totais = []
  const base = new Date()
  for (let i = 0; i < 6; i++) {
    const d = new Date(base.getFullYear(), base.getMonth() + i, 1)
    meses.push(d.toLocaleString('pt-BR', { month: 'short' }).replace('.', ''))
    const totalMes = ativas.value.reduce((s, a) => new Date(a.dataFim) >= d ? s + (a.valor || 0) : s, 0)
    totais.push(totalMes)
  }
  return { meses, totais }
})

const barChartData = computed(() => ({
  labels: previsaoAnual.value.meses,
  datasets: [{ label: 'Gasto Previsto', backgroundColor: '#7C3AED', data: previsaoAnual.value.totais, borderRadius: 8 }]
}))

const barChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: { beginAtZero: true, grid: { color: isDarkMode.value ? 'rgba(148,163,184,.1)' : 'rgba(226, 232, 240, 1)' }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } },
    x: { grid: { display: false }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } }
  },
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => `Gasto: ${formatCurrency(ctx.parsed.y)}` } } }
}))

onMounted(() => {
  verificarERenovarAssinaturas()
  checkDevice()
  window.addEventListener('resize', checkDevice)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkDevice)
})
</script>

<style scoped>
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20;
}
.card {
  @apply rounded-2xl bg-white dark:bg-slate-900/70 ring-1 ring-slate-200/80 dark:ring-slate-800/80 shadow-lg hover:shadow-xl transition-all duration-300;
}
.card-header {
  @apply flex items-center gap-4 px-5 py-4;
}
.panel-title {
  @apply text-base font-semibold text-slate-800 dark:text-slate-200;
}
.section-head {
  @apply flex items-center justify-between pb-2 border-b border-slate-200/80 dark:border-slate-800;
}
.section-title {
  @apply text-xl font-bold tracking-tight text-slate-900 dark:text-white;
}
.empty-state {
  @apply h-full grid place-content-center text-center text-slate-500 dark:text-slate-400 gap-2;
}
.info-item {
  @apply flex items-center gap-3 p-3 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-50/50 dark:bg-slate-800/40;
}
.info-icon {
  @apply text-base text-slate-500 dark:text-slate-400;
}
.info-k {
  @apply text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400;
}
.info-v {
  @apply text-sm font-medium text-slate-800 dark:text-slate-200;
}
.btn {
  @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60;
}
.btn-primary {
  @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20;
}
.btn-secondary {
  @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm;
}
.btn-secondary.btn-sm {
  @apply py-2 px-4 text-xs;
}
.btn-danger {
  @apply btn bg-rose-600 hover:bg-rose-700 text-white;
}
.btn-action-icon {
  @apply h-8 w-8 grid place-items-center rounded-lg transition-colors text-slate-500 dark:text-slate-400 hover:bg-slate-200/70 dark:hover:bg-slate-700/50;
}
.badge {
  @apply inline-flex items-center px-2.5 py-1 text-[10px] font-bold rounded-full ring-1;
}
.segmented-control {
  @apply inline-flex rounded-2xl p-1 bg-slate-200/70 dark:bg-slate-800/80;
}
.segmented-control button {
  @apply inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-semibold rounded-xl transition-all;
}
.segmented-control button.active {
  @apply bg-white dark:bg-slate-700 text-[#3C096C] dark:text-[#D9BFFB] shadow-md;
}
.segmented-control button:not(.active) {
  @apply text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.form-input,
.form-select {
  @apply w-full px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-800/70 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A];
}
.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5;
}
.modal-panel {
  @apply bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full ring-1 ring-black/10 dark:ring-white/10;
}
.modal-header {
  @apply px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/60 flex justify-between items-center;
}
.modal-title {
  @apply text-lg font-semibold text-slate-800 dark:text-slate-100;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity .2s ease, transform .2s ease
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(.98)
}
</style>
